/**
 * AiUsage — token usage tracking for every Bedrock model invocation across the app.
 *
 * Storage layout (single DynamoDB table "AiUsage", atomic ADD counters, no read-modify-write):
 *   User monthly total: pk USER#<userId>#<month>   sk TOTAL#<feature>#<family>#<initiatedBy>
 *   User daily:         pk USER#<userId>#<month>   sk DAY#<day>#<feature>#<family>#<initiatedBy>
 *   Platform daily:     pk PLATFORM#<month>         sk DAY#<day>#<feature>#<family>#<initiatedBy>
 *   User monthly per-flow (optional): pk USER#<userId>#<month>   sk FLOW#<flowId>#<feature>#<family>#<initiatedBy>
 *
 * The per-flow item is written ONLY when an event carries both `flowId` and `userId` —
 * it is a fourth item alongside the three above, never a replacement for the TOTAL one.
 * `flowId` is how a nested feature run (FLOW_SUMMARY, FLOW_CLASSIFICATION, FLOW_RULES,
 * FLOW_AI_STEP, FLOW_AGENT_TASK) is attributed back to the flow (agent) that ran it —
 * see `runWithAiUsageFeature` in `aiUsageScope.ts`, which inherits it from the active
 * scope exactly as it inherits `userId`/`teamId`. Without this item, every flow's usage
 * merges into the same feature-keyed TOTAL row and per-flow cost is unrecoverable.
 *
 * GSI TeamIndex (gsi1pk=TEAM#<teamId>#<month>, gsi1sk=<sk>) is populated on every USER
 * item (including the per-flow one) whenever teamId is present, so team-scoped rollups
 * can Query instead of Scan.
 *
 * Every event also carries `AI_FEATURE_CREDIT_POLICY[feature]`, written as a non-key
 * `creditPolicy` attribute on every USER item — Phase 1 records it, nothing is
 * debited or blocked on it yet (see docs/AI_CREDITS_PLAN.md).
 *
 * Every event also carries its computed cost as non-key `creditsSpent` (µC) and
 * `usdSpent` (µUSD) attributes on every row above — measurement, not billing, so
 * this happens unconditionally, independent of whether a wallet is ever debited. A row
 * written before these attributes existed carries neither; treat their absence as zero,
 * not an error.
 *
 * On a non-production stage every `pk`/`gsi1pk` is prefixed `STAGE#<NODE_ENV>#` —
 * staging and prod share this table (no per-stage table), so the prefix is the only
 * isolation between staging traffic and real numbers.
 */

export enum AiFeature {
  ASSISTANT = 'ASSISTANT',
  FLOW_AGENT_TASK = 'FLOW_AGENT_TASK',
  FLOW_CLASSIFICATION = 'FLOW_CLASSIFICATION',
  FLOW_RULES = 'FLOW_RULES',
  FLOW_SUMMARY = 'FLOW_SUMMARY',
  FLOW_GENERATION = 'FLOW_GENERATION',
  FLOW_AI_STEP = 'FLOW_AI_STEP',
  AGENT_GENERATION = 'AGENT_GENERATION',
  DOBOX_GENERATION = 'DOBOX_GENERATION',
  DOBOX_RULES = 'DOBOX_RULES',
  DOBOX_PROCESSING = 'DOBOX_PROCESSING',
  DOBOX_SUGGESTED_ACTIONS = 'DOBOX_SUGGESTED_ACTIONS',
  DOBOX_DELEGATE = 'DOBOX_DELEGATE',
  DOBOX_PREPARE = 'DOBOX_PREPARE',
  WORKFLOW_RECOMMENDER = 'WORKFLOW_RECOMMENDER',
  CHAT_AGENT = 'CHAT_AGENT',
  LLM_ENGINE = 'LLM_ENGINE',
  RAG = 'RAG',
  EMBEDDING = 'EMBEDDING',
  DRAFTING = 'DRAFTING',
  BRAIN_WRITING = 'BRAIN_WRITING',
  ARTIFACT_ASK_BRAIN = 'ARTIFACT_ASK_BRAIN',
  TRIGGER_SCENARIOS = 'TRIGGER_SCENARIOS',
  OBSERVABILITY = 'OBSERVABILITY',
  EVALUATION = 'EVALUATION',
  API_REQUEST = 'API_REQUEST',
  MCP_TOOL = 'MCP_TOOL',
  TEAM_REQUEST = 'TEAM_REQUEST',
  UNATTRIBUTED = 'UNATTRIBUTED',
}

export enum AiModelFamily {
  OPUS = 'OPUS',
  SONNET = 'SONNET',
  HAIKU = 'HAIKU',
  NOVA = 'NOVA',
  TITAN_EMBED = 'TITAN_EMBED',
  /** typesafe.ai's external decision model (see aiExternalModels.ts) — never a Bedrock id. */
  JEV = 'JEV',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Whether a feature's usage debits a team/user credit wallet once enforcement
 * ships (Phase 2 — see docs/AI_CREDITS_PLAN.md). Phase 1 only records this
 * alongside every usage event; nothing is debited or blocked yet.
 */
export enum AiCreditPolicy {
  BILLABLE = 'BILLABLE',
  EXEMPT = 'EXEMPT',
}

/**
 * Exhaustive by construction: `Record<AiFeature, AiCreditPolicy>` fails to
 * compile if a new `AiFeature` member has no policy assigned here.
 */
/**
 * FLOW_GENERATION, AGENT_GENERATION, DOBOX_GENERATION and TRIGGER_SCENARIOS are EXEMPT
 * even though the tokens are real spend: creating a flow, an agent, or a proposed DoBox is
 * product onboarding, not consumption, and is deliberately free to try. Usage is still
 * recorded in full (feature, model, tokens, `creditsSpent`) — only the wallet debit is
 * skipped, via the existing EXEMPT branch in `debitCreditsForUsage`.
 */
export const AI_FEATURE_CREDIT_POLICY: Record<AiFeature, AiCreditPolicy> = {
  [AiFeature.ASSISTANT]: AiCreditPolicy.BILLABLE,
  [AiFeature.FLOW_AGENT_TASK]: AiCreditPolicy.BILLABLE,
  [AiFeature.FLOW_CLASSIFICATION]: AiCreditPolicy.BILLABLE,
  [AiFeature.FLOW_RULES]: AiCreditPolicy.BILLABLE,
  [AiFeature.FLOW_SUMMARY]: AiCreditPolicy.BILLABLE,
  [AiFeature.FLOW_GENERATION]: AiCreditPolicy.EXEMPT,
  [AiFeature.FLOW_AI_STEP]: AiCreditPolicy.BILLABLE,
  [AiFeature.AGENT_GENERATION]: AiCreditPolicy.EXEMPT,
  [AiFeature.DOBOX_GENERATION]: AiCreditPolicy.EXEMPT,
  [AiFeature.DOBOX_RULES]: AiCreditPolicy.BILLABLE,
  [AiFeature.DOBOX_PROCESSING]: AiCreditPolicy.BILLABLE,
  [AiFeature.DOBOX_SUGGESTED_ACTIONS]: AiCreditPolicy.BILLABLE,
  // Debit stays off via IS_CREDIT_DEBIT_ENABLED (recordAiUsage.ts) until the worker ships it.
  [AiFeature.DOBOX_DELEGATE]: AiCreditPolicy.BILLABLE,
  [AiFeature.DOBOX_PREPARE]: AiCreditPolicy.EXEMPT,
  [AiFeature.WORKFLOW_RECOMMENDER]: AiCreditPolicy.BILLABLE,
  [AiFeature.CHAT_AGENT]: AiCreditPolicy.BILLABLE,
  [AiFeature.LLM_ENGINE]: AiCreditPolicy.BILLABLE,
  [AiFeature.RAG]: AiCreditPolicy.BILLABLE,
  [AiFeature.EMBEDDING]: AiCreditPolicy.EXEMPT,
  [AiFeature.DRAFTING]: AiCreditPolicy.BILLABLE,
  [AiFeature.BRAIN_WRITING]: AiCreditPolicy.BILLABLE,
  [AiFeature.ARTIFACT_ASK_BRAIN]: AiCreditPolicy.BILLABLE,
  [AiFeature.TRIGGER_SCENARIOS]: AiCreditPolicy.EXEMPT,
  [AiFeature.OBSERVABILITY]: AiCreditPolicy.EXEMPT,
  [AiFeature.EVALUATION]: AiCreditPolicy.EXEMPT,
  [AiFeature.API_REQUEST]: AiCreditPolicy.BILLABLE,
  [AiFeature.MCP_TOOL]: AiCreditPolicy.BILLABLE,
  [AiFeature.TEAM_REQUEST]: AiCreditPolicy.BILLABLE,
  [AiFeature.UNATTRIBUTED]: AiCreditPolicy.EXEMPT,
};

export enum AiUsageInitiator {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

/**
 * Substrings matched against both raw Bedrock model ids
 * (e.g. "us.anthropic.claude-haiku-4-5-20251001-v1:0") and inference-profile ARNs
 * (e.g. "arn:aws:bedrock:us-east-1:...:inference-profile/us.anthropic.claude-opus-4-6-v1").
 * Order matters: checked top to bottom, first match wins.
 *
 * Deliberately NOT unified with `aiBedrockModels.ts`'s version-keyed catalog: that catalog has
 * no Nova entry (no non-test caller today), so routing family resolution through it would
 * turn a real Nova modelId's family from NOVA into UNKNOWN — a credit-weighting behavior
 * change this file pins in its own spec. Kept as two independent matchers instead.
 */
const MODEL_FAMILY_MATCHERS: ReadonlyArray<[string, AiModelFamily]> = [
  ['opus', AiModelFamily.OPUS],
  ['sonnet', AiModelFamily.SONNET],
  ['haiku', AiModelFamily.HAIKU],
  ['nova', AiModelFamily.NOVA],
  ['titan-embed', AiModelFamily.TITAN_EMBED],
  ['jev', AiModelFamily.JEV],
];

export const resolveModelFamily = (modelId: string): AiModelFamily => {
  const normalizedModelId = modelId.toLowerCase();

  const match = MODEL_FAMILY_MATCHERS.find(([substring]) =>
    normalizedModelId.includes(substring),
  );

  return match ? match[1] : AiModelFamily.UNKNOWN;
};

// ASSISTANT vs AGENT is determined by whether the AgentRun carries an agentTemplateId, not a separate flag.
export enum AiCreditExecutionKind {
  FLOW = 'FLOW',
  ASSISTANT = 'ASSISTANT',
  AGENT = 'AGENT',
}

export interface AiUsageEvent {
  /** Omitted for a call the gateway cannot attribute to a user — only the platform item is written. */
  userId?: string;
  teamId?: string;
  /**
   * The flow (agent) this event's usage is attributed to. Present only for a call made
   * within an `AiUsageScope` opened with one (see `AiUsageScope.flowId` in
   * `aiUsageScopeStorage.ts`) — `recordAiUsage` writes an extra per-flow item only when this
   * AND `userId` are both set.
   */
  flowId?: string;
  feature: AiFeature;
  subFeature?: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  /** Number of model invocations this event represents. Defaults to 1. */
  invocations?: number;
  initiatedBy: AiUsageInitiator;
  /**
   * The `AiUsageScope` run this event was buffered under (see aiUsageScopeStorage.ts).
   * Omitted for `recordUnattributedUsage`'s platform-only event, since
   * `UNATTRIBUTED` is EXEMPT and never debits a wallet. Consumed by
   * `recordAiUsage`'s debit step as `${scopeRunId}#${modelId}#${subFeature ?? ''}`
   * — the idempotency key for the credit movement.
   */
  scopeRunId?: string;
  // Absent for usage outside any tracked execution — it still debits, it just writes no statement row.
  executionId?: string;
  executionKind?: AiCreditExecutionKind;
  /** Epoch millis the execution started — also the statement row's sort-key prefix, so every debit of one execution lands on the same row. */
  executionStartedAt?: number;
  /** Set only when the execution is a delegated custom-agent run (AGENT kind). */
  agentTemplateId?: string;
  /** Set only for an ASSISTANT/AGENT execution — the AgentRun's conversation, so the statement can name the row. */
  conversationId?: string;
}
